import { debounce, get } from "lodash";
import { useEffect } from "react";

import { FilterFieldOptionsHookResult } from "~/features/filter";
import { useGetLocationData } from "~/hooks/locationData";
import { useGetFileLinksLazyQuery } from "~/schemas/generated/graphql";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";

/**
 * Пользователи, загрузившие файлы в текущий каталог ПКД
 * (логика перенесена из старого PKDFilterForm). Служебная опция
 * «Без пользователя» не нужна — у Filter.Root есть allowClear.
 */
export const useFileUsersOptions = (): FilterFieldOptionsHookResult => {
  const { event } = useGetLocationData();
  const currentDealerId = useCurrentDealerId();

  const [getUsersForSelectQuery, { data, loading, fetchMore }] =
    useGetFileLinksLazyQuery({
      variables: {
        page: 0,
        DealerId: currentDealerId,
        catalog: event,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    getUsersForSelectQuery();
  }, []);

  const options = get(data, "UserGetter.getUsers.items", []).map(
    ({ id, fullname }) => ({
      value: String(id),
      label: fullname,
    }),
  );

  const onScrollBottom = () => {
    fetchMore({
      variables: {
        page: Math.ceil(options.length / 25),
      },
      updateQuery: ({ UserGetter }, { fetchMoreResult }) => {
        if (!fetchMoreResult) return { UserGetter };
        return {
          UserGetter: {
            ...UserGetter,
            getUsers: {
              ...UserGetter.getUsers,
              items: [
                ...UserGetter.getUsers.items,
                ...get(fetchMoreResult, "UserGetter.getUsers.items", []),
              ],
            },
          },
        };
      },
    });
  };

  const searchOptions = debounce((searchText: string) => {
    getUsersForSelectQuery({
      variables: {
        page: 0,
        searchField: searchText.length > 0 ? "nameOrId" : "",
        searchText,
      },
    });
  });

  return { options, loading, onScrollBottom, searchOptions };
};
